import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import SearchInput from '../SearchInput';
import styles from './NavigationSearch.module.scss';

const NavigationSearch = ({
    id = 'navigation',
    searchPageUrl = '',
    isVisible = false,
    onClose = () => {},
    onToggle = () => {},
    modifier = 'Navigation',
}) => {
    const { t } = useTranslation('common');

    const formRef = useRef();
    const containerRef = useRef();

    const [input, setInput] = useState('');

    useEffect(() => {
        if (modifier === 'NavigationMobile') {
            return;
        }

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible, onClose, modifier]);

    const submitFormTrigger = () => {
        if (formRef && formRef?.current) {
            formRef.current.submit();
        }
    };

    const searchId = `${id}-search`;

    const navigationSearchClasses = classNames(styles['NavigationSearch'], {
        [styles['NavigationSearch--IsVisible']]: isVisible,
        [styles[`NavigationSearch--${modifier}`]]: modifier,
    });

    const buttonClasses = classNames(styles['NavigationSearch__ToggleButton'], {
        [styles['NavigationSearch__ToggleButton--Hidden']]: isVisible,
    });

    return (
        <div className={navigationSearchClasses} ref={containerRef}>
            <button
                className={buttonClasses}
                onClick={onToggle}
                onKeyUp={(e) => {
                    const DOWN = 40;
                    const ENTER = 13;
                    const SPACE = 32;
                    if ([DOWN, ENTER, SPACE].indexOf(e.keyCode) > -1) {
                        onToggle();
                    }
                }}
                aria-hidden={isVisible}>
                <span className={styles['NavigationSearch__ButtonText']}>{t('search.button')}</span>
            </button>
            <form className={styles['NavigationSearch__Wrap']} action={searchPageUrl} ref={formRef}>
                <label htmlFor={searchId} className="sr-only">
                    {t('search.headlineGlobal')}
                </label>
                <SearchInput
                    id={searchId}
                    name="query"
                    isVisible={isVisible}
                    isInNavigation={true}
                    modifier={modifier}
                    setInputChanged={(value) => setInput(value)}
                    submitFormTrigger={submitFormTrigger}
                />
            </form>
        </div>
    );
};

NavigationSearch.propTypes = {
    id: PropTypes.string,
    searchPageUrl: PropTypes.string,
    isVisible: PropTypes.bool,
    onClose: PropTypes.func,
    onToggle: PropTypes.func,
    modifier: PropTypes.oneOf(['Navigation', 'NavigationMobile']),
};

export default NavigationSearch;
